import React, { useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { ProductListDto, ProductListItemDto } from 'models/dtoTypes';
import axios from 'axios';
import { get } from 'lodash';
import { date2str } from 'utils/date';
import { styled } from '@mui/material/styles';
import ResourceDetailContainer, { ITab } from 'kosmos/components/ResourceDetailContainer';
import Notice from 'kosmos/components/Notice';
// import Link from 'components/Link';
import { GetProductListDetail, UpdateProductList } from 'services/productList';
import generateNonce from 'utils/generateNonce';
import { getCountryLabel, countryOptions } from 'utils/countries';
import { IColumn } from 'kosmos/components/ResourceTable';
import { useTranslation } from 'react-i18next';
import ProductListEditTable from './_productListEditTable';
import ProductListVerificationTable from './_productListVerificationTable';

const MINUTE = 60 * 1000;
const getLocalTime = (time: string) =>
  new Date(+new Date(time) - new Date().getTimezoneOffset() * MINUTE);

const PREFIX = 'ProductListDetailPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export interface ICountryOption {
  label: string;
  alpha3: string;
}

function ProductListDetailPage(props: RouteComponentProps<{ id: string }>): JSX.Element {
  const id = props.match.params.id;
  const [openCreatedNotice, setOpenCreatedNotice] = React.useState(false);
  const [tabId, setTabId] = React.useState('details');
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [countries, setCountries] = React.useState<ICountryOption[]>([]);
  const { t } = useTranslation('default');

  const fetchCountries = () => {
    const options = countryOptions();
    let formattedOptions = options.map((item) => ({
      label: item.label || '',
      alpha3: item.alias || '',
    }));
    formattedOptions = formattedOptions.filter((a) => a.alpha3 !== 'world');
    setCountries(formattedOptions);
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleFetch = async (): Promise<ProductListDto> => {
    const productList = await GetProductListDetail(id);
    return productList;
  };

  const handleCloseCreatedNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenCreatedNotice(false);
  };

  const generateProductColumns = (): IColumn<ProductListItemDto>[] => {
    return [
      {
        label: t(''),
        sortIndex: '',
        render: () => '',
      },
      {
        label: t('Company Name'),
        sortIndex: 'companyName',
        render: (item) => item.companyName || '',
      },
      {
        label: t('country'),
        dataIndex: 'countryCode',
        render: (item) => getCountryLabel(item.countryCode || ''),
      },
      {
        label: t('HS Code(6)'),
        dataIndex: 'hsCode6',
        render: (item) => {
          return (
            <>
              <div>{item.hsCode6 || ''}</div>
              {item.productDescription && <div>{item.productDescription}</div>}
            </>
          );
        }
      },
      {
        label: t('Alignment'),
        sortIndex: 'alignment',
        render: (item) => item.alignment as string || '',
      },
      {
        label: t('Justification'),
        sortIndex: 'justification',
        render: (item) => item.justification as string || '',
      },
      {
        label: t('Status'),
        sortIndex: 'scoringState',
        render: (item) => item.scoringState as string || '',
      },
    ];
  };
  const generateTabs = (): ITab<ProductListDto>[] => [
    {
      id: 'details',
      title: t('details'),
      getUiSchema: () => ({
        fields: [
          {
            label: t('id'),
            name: 'id',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('name'),
            name: 'name',
            widget: 'text',
          },
          {
            label: t('description'),
            name: 'description',
            widget: 'text',
          },
          {
            label: t('last-score-triggered-at'),
            formatter: (datestr: string) => {
              return datestr == '0001-01-01T00:00:00'
                ? t('not-triggered-yet')
                : date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm');
            },
            name: 'lastTriggeredAt',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('created-at'),
            formatter: (datestr: string) => date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm'),
            name: 'created',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('last-updated-at'),
            name: 'updated',
            formatter: (datestr: string) => date2str(getLocalTime(datestr), 'DD MMM YYYY, HH:mm'),
            readonly: true,
            widget: 'text',
          },
        ],
      }),
    },
    {
      id: 'Products',
      title: t('Products'),
      render: (productList: ProductListDto) => {
        return (
          <div>
            <ProductListEditTable
              countries={countries}
              productListId={productList.id || ''}
              onErrorResponse={handleError}
            />
          </div>
        );
      },
    },
    {
      id: 'verifiedCompanies',
      title: t('scoring-results'),
      render: (productList: ProductListDto) => {
        return (
          <div>
            <ProductListVerificationTable
              countries={countries}
              onErrorResponse={handleError}
              columns={generateProductColumns()}
              productListId={productList.id || ''}
            />
          </div>
        );
      },
    },
  ];

  const handleUpdate = async (attrs: {
    name?: string;
    display?: string;
  }): Promise<ProductListDto> => {
    const org = await UpdateProductList(id, attrs);
    setNonce(generateNonce());
    return org;
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      // console.log(e.response?.data);
      const errorMessage =
        get(e.response?.data, 'message') ||
        get(e.response?.data, 'title') ||
        t('something-went-wrong');
      setOpenCreatedNotice(true);
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setOpenCreatedNotice(true);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  return (
    <Root>
      <ResourceDetailContainer
        key={nonce}
        tabs={generateTabs()}
        breadcrumbText={t('Product Lists')}
        breadcrumbUrl="/productLists"
        onTabChanged={setTabId}
        onFetch={handleFetch}
        onUpdate={handleUpdate}
        currentTabId={tabId}
      />
      {noticeData && (
        <Notice
          open={openCreatedNotice}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseCreatedNotice}
        />
      )}
    </Root>
  );
}

export default withRouter(ProductListDetailPage);
